/* eslint-disable @typescript-eslint/no-explicit-any */

import axios, { AxiosResponse, AxiosError, AxiosRequestConfig } from "axios";
import { baseUrl } from "../lib";
import Cookies from "js-cookie";
import {
  AlreadyRewardResponse,
  BindResponse,
  ChildrenInfo,
  CreateParentAccountResponse,
  GetParentInfoByCode,
  InfoData,
  LoginResponse,
  RequestRewardResponse,
} from "../lib/type";

class ApiClient {
  private readonly baseUrl = baseUrl;

  constructor() {}

  private handleResponse<T>(response: AxiosResponse<T>): T {
    if (response.status >= 200 && response.status < 300) {
      return response.data;
    } else {
      throw new Error(`API request failed with status ${response.status}`);
    }
  }
  private handleError(error: AxiosError): Promise<never> {
    if (error.response) {
      return Promise.reject(
        new Error(`API request failed with status ${error.response.status}`)
      );
    } else if (error.request) {
      return Promise.reject(new Error("No response received from the server"));
    } else {
      return Promise.reject(new Error(`Request failed: ${error.message}`));
    }
  }
  async request<T>(
    method: "get" | "post" | "put" | "delete",
    dataOrParams: any = {},
    signal?: AbortSignal
  ): Promise<T> {
    const config: AxiosRequestConfig = {
      method,
      url: this.baseUrl,
      signal,
    };

    if (method === "get") {
      config.params = dataOrParams;
    } else {
      config.data = dataOrParams;
    }

    try {
      const response = await axios(config);
      return this.handleResponse(response);
    } catch (error: any) {
      return this.handleError(error); // Propagate the error as a rejected promise
    }
  }
  async login(username: string, password: string): Promise<LoginResponse> {
    const response = await this.request<LoginResponse>(
      "post",
      {
        cmd: "gms_GmsParentAdmin.login",
        name: username,
        password,
      },
      undefined
    );

    return response;
  }
  async bindChildCode(child_code: string): Promise<BindResponse> {
    const response = await this.request<BindResponse>(
      "post",
      {
        cmd: "gms_GmsParentAdmin.bindChildCode",
        child_code,
      },
      undefined
    );

    return response;
  }
  async parentGetRewardForChild(
    parent_code: string,
    sec: number
  ): Promise<RequestRewardResponse> {
    const response = await this.request<RequestRewardResponse>("post", {
      cmd: "gms_GmsParentAdmin.parentGetRewardForChild",
      cost_sec: sec,
      parent_code: parent_code,
    });

    return response;
  }
  async alreadyGetRewardToday(
    parent_code: string
  ): Promise<AlreadyRewardResponse> {
    const response = await this.request<AlreadyRewardResponse>("post", {
      cmd: "gms_GmsParentAdmin.alreadyGetRewardToday",
      parent_code,
      child_code: Cookies.get("child_code"),
    });

    return response;
  }
  async getParentInfoByCode(parent_code: string): Promise<GetParentInfoByCode> {
    const response = await this.request<GetParentInfoByCode>("post", {
      cmd: "gms_GmsParentAdmin.getParentInfoByCode",
      parent_code,
      child_code: Cookies.get("child_code"),
    });

    return response;
  }
  async parentGetChildrenInfo(parent_code: string): Promise<ChildrenInfo> {
    const response = await this.request<ChildrenInfo>("post", {
      cmd: "gms_GmsParentAdmin.getChildrenInfo",
      parent_code: parent_code,
    });

    return response;
  }
  async getParentInformation(parent_code: string): Promise<InfoData> {
    const response = await this.request<InfoData>("post", {
      cmd: "gms_GmsParentAdmin.getParentInformation",
      parent_code: parent_code,
    });
    return response;
  }
  async sendEmail(email: string, parent_code: string): Promise<InfoData> {
    const response = await this.request<InfoData>("post", {
      cmd: "gms_GmsParentAdmin.sendEmail",
      email: email,
      parent_code: parent_code,
    });
    return response;
  }

  async getLoginLink(redirectLink: string): Promise<string> {
    const response: string = await this.request("post", {
      cmd: "gms_GoogleSSO.getLoginLink",
      state: "hat_p_connect",
      redirect_uri: redirectLink,
    });

    return response;
  }
  async removeChild(child_id: string, parentCode: string): Promise<string> {
    const response: string = await this.request("post", {
      cmd: "gms_GmsParentAdmin.removeChild",
      child_id,
      parent_code: parentCode,
    });

    return response;
  }
  async removeAll(parentCode: string): Promise<string> {
    const response: string = await this.request("post", {
      cmd: "gms_GmsParentAdmin.removeChild",
      parent_code: parentCode,
    });

    return response;
  }
  async createParentAccount(
    phoneNumber: string
  ): Promise<CreateParentAccountResponse> {
    const response = await this.request<CreateParentAccountResponse>("post", {
      cmd: "ModHandle.createParentAccount",
      phone_number: phoneNumber,
    });

    return response;
  }

  async loginParent(phoneNumber: string, pin: string): Promise<any> {
    const response: string = await this.request("post", {
      cmd: "ModHandle.loginParent",
      phone_number: phoneNumber,
      pin,
    });

    return response;
  }
  async setParentPIN(pin: string, parent_code: string): Promise<any> {
    const response: string = await this.request("post", {
      cmd: "ModHandle.setParentPIN",
      pin,
      parent_code,
    });

    return response;
  }
}

export const apiClient = new ApiClient();
